import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import useModelManagementStore from '../../modelManagement/store/useModelManagementStore';
import useProjectManagementStore from '../store/useProjectManagementStore';
import useProjectManagementHook from '../hooks/useProjectManagementHook';
import { useNavigate } from "react-router-dom";
import './ProjectCard.css';
import { useSwal } from '../../../../components/Notifications/swal/SwalCom';

const ProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const dropdownMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        !event.target.closest('.labelflow-dropdown-trigger') &&
        !event.target.closest('.labelflow-dropdown-portal')
      ) {
        setIsMenuOpen(false);
        setDropdownAnchor(null);
      }
    };
    const handleScroll = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
        setDropdownAnchor(null);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('click', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isMenuOpen]);
  const { setIsModalOpen, setProjectInfo, setPopupType } = useProjectManagementStore();
  const { handler } = useProjectManagementHook();
  const { setProjectId } = useModelManagementStore();
  const { swalConfirm } = useSwal();

  const clearLabelSession = () => {
    try {
      sessionStorage.removeItem('lf-detection-model');
      sessionStorage.removeItem('lf-detection-version-id');
      sessionStorage.removeItem('lf-classification-model');
      sessionStorage.removeItem('lf-classification-version-id');
    } catch (err) {
      console.warn('Unable to clear label session info', err);
    }
  };

  const handleProjectClick = () => {
    clearLabelSession();
    setProjectId(project?._id ?? null);
    navigate("/ModelManagement", { state: { projectId: project._id } });
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
    setProjectInfo(project);
    setIsMenuOpen(false);
  };

  const handleAddModelPopup = (e) => {
    e.stopPropagation();
    setProjectInfo(project);
    setProjectId(project?._id ?? null);
    setPopupType("model");
    setIsModalOpen(true);
    setIsMenuOpen(false);
  };

  const handleDeleteProject = (e) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    setDropdownAnchor(null);
    swalConfirm(
      "Delete project",
      `Are you sure you want to delete the project "${project?.name}"?`,
      async (result) => {
        if (result?.isConfirmed) {
          await handler.handleDeleteProject({ 
            "id": project?._id || "0"
          });
        }
      },
    );
  };

  const handleDetailClick = (e) => {
    e.stopPropagation();
    clearLabelSession();
    navigate("/ModelManagement", { state: { projectId: project._id } });
    setIsMenuOpen(false);
  };

  const modelsArray = Array.isArray(project?.models) ? project.models : [];
  const modelVersions = modelsArray.flatMap((model) => model.versions || []);

  let completedCount = modelVersions.filter((ver) => (ver?.trainingStatus || "").toLowerCase() === "complete").length;
  let inProgressCount = modelVersions.filter((ver) => (ver?.trainingStatus || "").toLowerCase() === "inprogress").length;
  let pendingCount = modelVersions.filter((ver) => {
    const status = (ver?.trainingStatus || "").toLowerCase();
    return !["complete", "inprogress"].includes(status);
  }).length;

  if (modelVersions.length === 0 && project.modelCounts) {
    completedCount = project.modelCounts.completed ?? project.modelCounts.complete ?? 0;
    inProgressCount =
      project.modelCounts.in_progress ??
      project.modelCounts.training ??
      project.modelCounts.inProgress ??
      0;
    pendingCount = project.modelCounts.pending ?? 0;
  }
  const modelTypes = project.modelTypes && project.modelTypes.length > 0
    ? project.modelTypes
    : ["No Model Types"];

  const totalModels =
    Object.values(project?.modelCounts || {}).reduce((sum, val) => sum + (Number(val) || 0), 0) ||
    modelsArray.length ||
    project.modelCounts?.total ||
    completedCount + inProgressCount + pendingCount;

  return (
    <div className="project-card" onClick={handleProjectClick}>
      <div className="project-card__accent-bar"></div>
      
      <div className="project-card__content">
        {/* Header Section */}
        <div className="project-card__header">
          <div className="project-card__info">
            <div className="project-card__title-wrapper">
              <div className="project-card__icon">
                <i className="fa fa-globe"></i>
              </div>
              <div className="project-card__title-info">
                <h3 className="project-card__title">{project.name}</h3>
                <p className="project-card__subtitle">
                  {totalModels} {totalModels === 1 ? 'model' : 'models'}
                </p>
              </div>
            </div>
            <p className="project-card__detail">{project.detail}</p>
          </div>

          {/* Dropdown Menu – master design (portal-based) */}
          <div className="labelflow-dropdown-trigger project-card__menu" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary labelflow-dropdown-btn"
              aria-haspopup="true"
              aria-expanded={isMenuOpen}
              aria-label="More options"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                const nextOpen = !isMenuOpen;
                setIsMenuOpen(nextOpen);
                setDropdownAnchor(nextOpen ? { rect } : null);
              }}
            >
              <i className="fa fa-ellipsis-h" aria-hidden />
            </button>

            {isMenuOpen && dropdownAnchor && (
              ReactDOM.createPortal(
                <div
                  className="labelflow-dropdown-portal"
                  ref={dropdownMenuRef}
                  style={{
                    position: 'fixed',
                    top: dropdownAnchor.rect.bottom + 8,
                    right: window.innerWidth - dropdownAnchor.rect.right,
                    zIndex: 1060,
                  }}
                >
                  <div className="labelflow-dropdown-menu">
                    <button type="button" className="labelflow-dropdown-item" onClick={handleEditClick}>
                      <i className="fa fa-edit" aria-hidden /> Edit Project
                    </button>
                    <button type="button" className="labelflow-dropdown-item" onClick={handleDetailClick}>
                      <i className="fa fa-info-circle" aria-hidden /> View Details
                    </button>
                    <button type="button" className="labelflow-dropdown-item" onClick={handleAddModelPopup}>
                      <i className="fa fa-plus" aria-hidden /> Add Model
                    </button>
                    <div className="labelflow-dropdown-divider" />
                    <button type="button" className="labelflow-dropdown-item labelflow-dropdown-item--danger" onClick={handleDeleteProject}>
                      <i className="fa fa-trash" aria-hidden /> Delete
                    </button>
                  </div>
                </div>,
                document.body,
              )
            )}
          </div>
        </div>

        {/* Model Types Section */}
        <div className="project-card__section">
          <p className="project-card__section-label">Model Types</p>
          <div className="project-card__badges">
            {modelTypes.map((type, idx) => (
              <span key={idx} className="project-card__badge">
                {type}
              </span>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="project-card__stats">
          {[
            {
              key: "completed",
              count: completedCount,
              icon: "fa-check",
              label: "Done",
              modifier: "success",
            },
            {
              key: "in_progress",
              count: inProgressCount,
              icon: inProgressCount > 0 ? "fa-spinner fa-spin" : "fa-spinner",
              label: "Running",
              modifier: "info",
              pulse: inProgressCount > 0,
            },
            {
              key: "pending",
              count: pendingCount,
              icon: "fa-hourglass-half",
              label: "Queued",
              modifier: "warning",
            },
          ].map((stat) => (
            <div key={stat.key} className={`project-card__stat project-card__stat--${stat.modifier}`}>
              <div className={`project-card__stat-icon ${stat.pulse ? 'project-card__stat-icon--pulse' : ''}`}>
                <i className={`fa ${stat.icon}`}></i>
              </div>
              <div className="project-card__stat-info">
                <p className="project-card__stat-value">
                  {stat.count}
                </p>
                <p className="project-card__stat-label">
                  {stat.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="project-card__hover-overlay"></div>
    </div>
  );
};

export default ProjectCard;